/// <amd-module name="VSSUI/Navigation/VssViewState" />
import { IFilterState } from "VSSUI/Utilities/Filter";
import { IObservableValue } from "VSS/Core/Observable";
import { IViewState, ViewState } from "../Utilities/ViewState";
import { IViewStateChangeEvent, ViewStateObservableValue } from "../Utilities/ViewState";
import { IViewOptionsValues } from "../Utilities/ViewOptions";
import { IVssPageContext } from "VSS/Platform/Context";
import { StateMergeOptions } from "VSS/Platform/History";
import { INavigationParameter, INavigationParameters, IObservableViewStateUrl, HistoryBehavior, ViewStateNavigation } from "./ViewStateNavigation";

/** @deprecated */
export interface IVssViewState<T> extends IViewState<T> {
    /**
     * Perform a bulk set of changes to the view state and make a single update
     * to the browser history -- either adding a new entry or replacing the existing entry.
     * The behavior specified here overrides the behavior for individual nav parameters.
     *
     * @param behavior Whether to add a new history entry or replace the existing entry after modifying the view state
     * @param updateViewStateCalback Method where state should be updated.
     */
    updateNavigationState: (behavior: HistoryBehavior, updateViewStateCallback: () => void) => void;

    /**
     * Create an observable for a URL on this view which gets updated whenever the view state has changed.
     *
     * @param state Values (query parameters and route values) to use when generating the url.
     * @param mergeOptions Options around whether the provided state just provides overrides for the current navigation
     *        state or a full set of state values. The default is to merge with the current navigation state.
     */
    createObservableUrl(state: { [key: string]: string }, mergeOptions?: StateMergeOptions, routeId?: string): IObservableViewStateUrl;

    /**
     * Removes navigation event listeners and disposes this view state
     */
    dispose(): void;
}

/**
 * Describes a value stored in view state that should be mapped to the page's URL
 */
/** @deprecated */
export interface IVssNavigationParameter {
    /**
     * The key of the item in the filter or view options state
     */
    key: string;

    /**
     * Optional navigation parameter name. The key is used by default, this only should
     * be specified if it is different.
     */
    navParamName?: string;

    /**
     * If true, the parameter value is a raw string. If false (the default), JSON.parse/stringify
     * is used to deserialize/serialize the string value. The default value of false can be used
     * for booleans, numbers, and complex objects.Use rawString: true to avoid quote characters
     * in the URL value for strings.
     */
    rawString?: boolean;

    /**
     * What to do when this value triggers a view-state change event. By default, the
     * URL is updated by replaceState.
     */
    behavior?: HistoryBehavior;

    /**
     * Custom serialization method to get the string value to put in the URL from
     * the view state value. When specified, rawString will be ignored during the serialization.
     */
    serialize?: (value: any) => string;

    /**
     * Custom deserializer to deserialize the raw state string to navigation parameter value.
     * When specified, rawString will be ignored during the deserialization.
     */
    deserialize?: (value: string) => any;
}

interface IObservableValueNavigationParameter extends IVssNavigationParameter {
    observable: IObservableValue<any>;
    defaultValue?: any;
}

/**
 * Base view state for VSS hubs
 */
/** @deprecated */
export class VssViewState<T> extends ViewState<T> implements IVssViewState<T> {
    private pageContext: IVssPageContext;
    private navigation: ViewStateNavigation<T>;
    private observableValueParams: { [changeEventName: string]: IObservableValueNavigationParameter };
    private observableUrls: IObservableViewStateUrl[];

    constructor(pageContext: IVssPageContext) {
        super();

        this.pageContext = pageContext;
        this.observableUrls = [];
        this.observableValueParams = {};
    }

    protected setupNavigation() {
        if (this.navigation) {
            this.navigation.dispose();
        }
        this.navigation = new ViewStateNavigation<T>(
            this.pageContext,
            this,
            this.notify.bind(this),
            this.getNavigationParameters(),
            true,
            this._getUpdateBehavior
        );
    }

    public dispose() {
        if (this.navigation) {
            this.navigation.dispose();
            delete this.navigation;
        }

        for (let observableUrl of this.observableUrls) {
            observableUrl.dispose();
        }

        this.observableUrls = [];
    }

    /**
     * Perform a bulk set of changes to the view state and make a single update
     * to the browser history -- either adding a new entry or replacing the existing entry.
     * The behavior specified here overrides the behavior for individual nav parameters.
     *
     * @param behavior Whether to add a new history entry or replace the existing entry after modifying the view state
     * @param updateViewStateCalback Method where state should be updated.
     */
    public updateNavigationState(behavior: HistoryBehavior, updateViewStateCallback: () => void) {
        if (this.navigation) {
            this.navigation.updateNavigationState(behavior, updateViewStateCallback);
        } else {
            updateViewStateCallback();
        }
    }

    /**
     * Create an observable for a URL on this view which gets updated whenever the view state has changed.
     *
     * @param state Values (query parameters and route values) to use when generating the url.
     * @param mergeOptions Options around whether the provided state just provides overrides for the current navigation
     *        state or a full set of state values. The default is to merge with the current navigation state.
     */
    public createObservableUrl(state: { [key: string]: string }, mergeOptions?: StateMergeOptions, routeId?: string): IObservableViewStateUrl {
        const observableUrl = this.navigation.createObservableUrl(state, mergeOptions, routeId);
        this.observableUrls.push(observableUrl);
        return observableUrl;
    }

    /**
     * Create an observable value object which updates this view state when its value changes
     *
     * @param changeEventName Name of the change-event to fire on the view-state
     * @param value The initial value of the observable
     */
    protected createObservableValue<T>(changeEventName: string, value?: T): IObservableValue<T> {
        return new ViewStateObservableValue<T>(this.notify.bind(this) as any, changeEventName, value);
    }

    /**
     * Creates an observable value object which updates this view state when its value changes
     * and is mapped to the specified URL navigation parameter.
     *
     * @param changeEventName Name of the change-event to fire on the view-state
     * @param paramName The name of the navigation parameter (route value or query parameter key)
     * @param isStringValue Is this a raw string value
     * @param behavior How to update the browser history when this value changes
     * @param value The initial value of the observable
     * @param defaultValue Optional default value of the observable. If the current value is undefined, then the view state is populated with this value.
     */
    protected createNavParamObservableValue<T>(
        changeEventName: string,
        paramName: string,
        isStringValue: boolean,
        behavior?: HistoryBehavior,
        value?: T,
        defaultValue?: T
    ): IObservableValue<T> {
        const observableValue = this.createObservableValue(changeEventName, value);
        this.observableValueParams[changeEventName] = {
            key: paramName,
            observable: observableValue,
            rawString: isStringValue,
            behavior: behavior,
            defaultValue: defaultValue
        };
        return observableValue;
    }

    protected getNavigationParameters(): INavigationParameters {
        const navParameters: INavigationParameters = {};

        // Add navigation params for registered observable values
        for (let key in this.observableValueParams) {
            const param = this.observableValueParams[key];
            navParameters[param.navParamName || param.key] = this.createObservableValueNavParam(param);
        }

        return navParameters;
    }

    protected getStringValueForNavParam(value: any, param: IVssNavigationParameter, defaultValue?: any) {
        if (value === null || value === undefined) {
            value = defaultValue;
        }

        if (param.serialize) {
            return param.serialize(value);
        }

        if (value === null || value === undefined) {
            return undefined;
        } else if (param.rawString) {
            return value.toString();
        } else {
            return JSON.stringify(value);
        }
    }

    protected getRawValueFromNavParamString(serializedValue: string, param: IVssNavigationParameter): any {
        let value: any;

        if (!serializedValue) {
            value = undefined;
        } else if (param.deserialize) {
            value = param.deserialize(serializedValue);
        } else if (param.rawString) {
            value = serializedValue;
        } else {
            try {
                value = JSON.parse(serializedValue);
            } catch (ex) {
                // Invalid value from URL
                value = undefined;
            }
        }

        return value;
    }

    protected updateFromNavParamString(
        existingValue: any,
        serializedValue: string,
        param: IVssNavigationParameter,
        updateCallback: (value: any) => void
    ) {
        const newValue = this.getRawValueFromNavParamString(serializedValue, param);
        this.updateFromNavParamValue(existingValue, newValue, updateCallback);
    }

    protected updateFromNavParamValue(existingValue: any, newValue: any, updateCallback: (value: any) => void) {
        if (newValue === undefined || newValue === null) {
            if (existingValue !== null && existingValue !== undefined) {
                updateCallback(undefined);
            }
        } else {
            if (existingValue !== newValue) {
                updateCallback(newValue);
            }
        }
    }

    protected updateFromNavigationState(parameter: string): void {
        if (this.navigation) {
            this.navigation.updateFromNavigationState(parameter);
        }
    }

    private createObservableValueNavParam(param: IObservableValueNavigationParameter): INavigationParameter {
        return {
            getValue: () => {
                return this.getStringValueForNavParam(param.observable.value, param, param.defaultValue);
            },
            setValue: (newValueString: string) => {
                let existingValue = param.observable.value;
                if (existingValue === null || existingValue === undefined) {
                    existingValue = param.defaultValue;
                }

                if (newValueString === undefined && param.defaultValue !== undefined) {
                    this.updateFromNavParamValue(existingValue, param.defaultValue, value => {
                        param.observable.value = value;
                    });
                } else {
                    this.updateFromNavParamString(existingValue, newValueString, param, value => {
                        if (value === undefined) {
                            value = param.defaultValue;
                        }
                        param.observable.value = value;
                    });
                }
            }
        };
    }

    private _getUpdateBehavior = (e: IViewStateChangeEvent<IViewOptionsValues | IFilterState | string>): HistoryBehavior => {
        return this.getUpdateBehavior(e);
    };

    protected getUpdateBehavior(e: IViewStateChangeEvent<IViewOptionsValues | IFilterState | string>): HistoryBehavior {
        let behavior = HistoryBehavior.none;

        let observableValueParam = this.observableValueParams[e.eventName];
        if (observableValueParam) {
            behavior = observableValueParam.behavior;
        }

        return behavior;
    }
}
